import "./security.css";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react";
import { useId, useRef, useState, type FormEvent } from "react";
import type { AccountSecurityManagement } from "../types";
import { authenticationActionError } from "./presentation";

export function PrimaryEmailCard({
  accountSecurity,
}: {
  accountSecurity: AccountSecurityManagement;
}) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState(() =>
    primaryEmailChangeOutcomeMessage(accountSecurity.emailChangeOutcome),
  );
  const [feedbackTone, setFeedbackTone] = useState<"error" | "success">(
    accountSecurity.emailChangeOutcome === "completed" ? "success" : "error",
  );
  const [newEmail, setNewEmail] = useState("");
  const [pending, setPending] = useState<"begin" | "cancel" | "resend" | null>(
    null,
  );
  const emailInput = useRef<HTMLInputElement>(null);
  const feedbackId = useId();
  const snapshot = accountSecurity.snapshot;
  const emailChange = snapshot?.emailChange;

  function requestConfirmation(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const normalized = newEmail.trim().toLowerCase();
    if (!isSyntacticallyValidEmail(normalized)) {
      setFeedbackTone("error");
      setFeedback("Enter a valid email address.");
      return;
    }
    if (normalized === snapshot?.email.toLowerCase()) {
      setFeedbackTone("error");
      setFeedback("Enter a different email address.");
      return;
    }
    setNewEmail(normalized);
    setFeedback("");
    setConfirmEmail(true);
  }

  async function begin(): Promise<void> {
    setPending("begin");
    setFeedback("");
    try {
      await accountSecurity.beginEmailChange(newEmail);
      setConfirmEmail(false);
      setEditing(false);
      setFeedbackTone("success");
      setFeedback(
        "Verification link queued. Your current email stays primary until the new one is verified.",
      );
    } catch (cause) {
      setConfirmEmail(false);
      setFeedbackTone("error");
      setFeedback(
        authenticationActionError(
          cause,
          "This email change could not be started. Your current email is unchanged.",
        ),
      );
    } finally {
      setPending(null);
    }
  }

  async function resend(): Promise<void> {
    setPending("resend");
    setFeedback("");
    try {
      await accountSecurity.resendEmailChange();
      setFeedbackTone("success");
      setFeedback(
        "A new verification link is queued. Older links no longer work.",
      );
    } catch (cause) {
      setFeedbackTone("error");
      setFeedback(
        authenticationActionError(
          cause,
          "A new verification link could not be queued.",
        ),
      );
    } finally {
      setPending(null);
    }
  }

  async function cancel(): Promise<void> {
    setPending("cancel");
    setFeedback("");
    try {
      await accountSecurity.cancelEmailChange();
      setConfirmCancel(false);
      setNewEmail("");
      setFeedbackTone("success");
      setFeedback("Email change canceled. Your verified email is unchanged.");
    } catch (cause) {
      setFeedbackTone("error");
      setFeedback(
        authenticationActionError(
          cause,
          "The pending email change could not be canceled.",
        ),
      );
    } finally {
      setPending(null);
    }
  }

  if (accountSecurity.state !== "ready" || snapshot === undefined) return null;

  return (
    <section className="pm-passport-card pm-primary-email-card">
      <header>
        <div>
          <h2>Primary email</h2>
          <p>The verified address used for sign-in and Account recovery.</p>
        </div>
        <small>Fresh sign-in required</small>
      </header>

      <div className="pm-auth-method pm-auth-primary-email">
        <span className="pm-auth-method-icon">
          <EnvelopeSimpleIcon size={21} weight="fill" />
        </span>
        <div>
          <strong>Verified primary</strong>
          <p>{snapshot.email}</p>
        </div>
        <small>
          {snapshot.emailVerified ? "Verified" : "Verification needed"}
        </small>
      </div>

      {emailChange !== undefined ? (
        <div className={`pm-email-change-pending ${emailChange.status}`}>
          <div>
            <span>PENDING NEW EMAIL</span>
            <strong>{emailChange.newEmail}</strong>
            <p>{primaryEmailDeliveryMessage(emailChange)}</p>
            <small>
              Your current verified email remains primary until completion.
            </small>
          </div>
          <div className="pm-email-change-actions">
            <button
              disabled={pending !== null}
              onClick={() => void resend()}
              type="button"
            >
              {pending === "resend" ? "Queuing link…" : "Resend link"}
            </button>
            <button
              disabled={pending !== null}
              onClick={() => setConfirmCancel(true)}
              type="button"
            >
              Cancel change
            </button>
          </div>
        </div>
      ) : editing ? (
        <form
          className="pm-email-change-form"
          noValidate
          onSubmit={requestConfirmation}
        >
          <label>
            <span>NEW EMAIL</span>
            <input
              aria-describedby={feedback ? feedbackId : undefined}
              aria-invalid={
                feedbackTone === "error" && feedback ? true : undefined
              }
              autoComplete="email"
              autoFocus
              disabled={pending !== null}
              onChange={(event) => {
                setNewEmail(event.currentTarget.value);
                setFeedback("");
              }}
              ref={emailInput}
              required
              type="email"
              value={newEmail}
            />
          </label>
          <div>
            <button
              onClick={() => {
                setEditing(false);
                setFeedback("");
              }}
              type="button"
            >
              Cancel
            </button>
            <button disabled={pending !== null} type="submit">
              Continue
            </button>
          </div>
        </form>
      ) : (
        <button
          className="pm-change-email-button"
          onClick={() => {
            setEditing(true);
            setFeedback("");
          }}
          type="button"
        >
          Change email
        </button>
      )}

      {confirmEmail ? (
        <div
          aria-label="Confirm primary email change"
          className="pm-security-confirmation"
          role="group"
        >
          <div>
            <strong>Send verification to {newEmail}?</strong>
            <p>{snapshot.email} remains primary until that link is used.</p>
          </div>
          <button
            onClick={() => {
              setConfirmEmail(false);
              queueMicrotask(() => emailInput.current?.focus());
            }}
            type="button"
          >
            Back
          </button>
          <button
            disabled={pending !== null}
            onClick={() => void begin()}
            type="button"
          >
            {pending === "begin" ? "Sending…" : "Send verification"}
          </button>
        </div>
      ) : null}

      {confirmCancel ? (
        <div
          aria-label="Confirm email change cancellation"
          className="pm-security-confirmation"
          role="group"
        >
          <div>
            <strong>Cancel this email change?</strong>
            <p>
              Every verification link for {emailChange?.newEmail} will stop
              working.
            </p>
          </div>
          <button onClick={() => setConfirmCancel(false)} type="button">
            Keep change
          </button>
          <button
            className="danger"
            disabled={pending !== null}
            onClick={() => void cancel()}
            type="button"
          >
            {pending === "cancel" ? "Canceling…" : "Cancel email change"}
          </button>
        </div>
      ) : null}

      {feedback ? (
        <p
          className={`pm-security-feedback ${feedbackTone}`}
          id={feedbackId}
          role={feedbackTone === "error" ? "alert" : "status"}
        >
          {feedback}
        </p>
      ) : null}
    </section>
  );
}

function primaryEmailDeliveryMessage(
  emailChange: NonNullable<
    AccountSecurityManagement["snapshot"]
  >["emailChange"] & {},
): string {
  if (
    emailChange?.status === "expired" ||
    emailChange?.deliveryStatus === "expired"
  ) {
    return "This verification link expired. Queue a new link to restart safely.";
  }
  if (emailChange?.deliveryStatus === "retrying") {
    return "Delivery is taking longer than usual. We will keep trying safely.";
  }
  if (emailChange?.deliveryStatus === "delivered") {
    return "Verification email delivered. Open the newest link to finish.";
  }
  if (emailChange?.deliveryStatus === "failed") {
    return "Delivery stopped after several attempts. Queue a new link to retry.";
  }
  return "Verification email queued. It may take a moment to arrive.";
}

function primaryEmailChangeOutcomeMessage(
  outcome: AccountSecurityManagement["emailChangeOutcome"],
): string {
  if (outcome === "completed") {
    return "Primary email changed. Other browser sessions were signed out.";
  }
  if (outcome === "conflict") {
    return "This email change could not be completed. Your current email is unchanged.";
  }
  if (outcome === "expired") {
    return "That verification link expired. Start a new email change from here.";
  }
  if (outcome === "invalid") {
    return "That verification link is no longer usable. Start a new email change from here.";
  }
  return "";
}

function isSyntacticallyValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
